import type {
  FirmAssumptionPresets,
  HouseholdFactFindDocument,
  ImportReviewBlocker,
  HouseholdProjectionPreviewResponse,
  IsoDate,
  PinnedAssumptionSetWire,
  SinceLastReport,
} from '@cf/shared';
import {
  ILLUSTRATIVE_NOT_ADVICE_DISCLAIMER,
  householdProjectionPreviewResponseSchema,
  importReviewBlockers,
  isoDate,
  projectionRequestFromDocument,
} from '@cf/shared';
import type { HouseholdProjectionResult } from '@cf/engine';
import { buildHouseholdInput, explainYear, makeClock, projectHousehold } from '@cf/engine';
import { rulesForTaxYear } from '@cf/tax';

/**
 * The builder's data seam. Persistence differs by environment (the API in a
 * real deployment, browser storage in the local dev/demo mode) but the maths
 * NEVER does: both paths run the identical pure engine on the identical
 * shared wire mapping (`buildHouseholdInput`), so a chart drawn locally is
 * byte-identical to the API preview for the same document.
 */
export interface HouseholdFactFindState {
  document: HouseholdFactFindDocument;
  version: number;
  updatedAt: string;
}

export interface HouseholdDataClient {
  /** Injected clock — no wall-clock reads anywhere near the projection. */
  getAsOfDate(): IsoDate;
  getStartYear(): number;
  loadFactFind(clientId: string): Promise<HouseholdFactFindState | null>;
  /**
   * GOAL 2026-09-05 D2.1 — what moved since the client's last EXPORTED report.
   * Required rather than optional: an optional method is one an implementation
   * can silently not have, and the panel would then render its empty state —
   * "no previous report on file" — which is a statement about the client, not
   * about the client library.
   */
  loadSinceLastReport(clientId: string): Promise<SinceLastReport | null>;
  /**
   * Replace (or create, expectedVersion null) the client's document.
   * Rejects with `FactFindConflictError` when the version moved.
   */
  saveFactFind(
    clientId: string,
    document: HouseholdFactFindDocument,
    expectedVersion: number | null,
  ): Promise<HouseholdFactFindState>;
}

/**
 * The plan is marked READY TO PRESENT (D3.4), so the API refused the write.
 *
 * Its own error because the alternative was worse than no error: the lock
 * answers 409, the client branched on the STATUS alone, and every locked save
 * was reported as `FactFindConflictError` — "This fact-find changed somewhere
 * else. Autosave has stopped — reload before editing further." That is false,
 * it is terminal for the session, and reloading does not help because the plan
 * is still locked. An adviser met it mid-meeting and had no way back.
 */
export class PlanLockedError extends Error {
  constructor() {
    super(
      'This plan is marked ready to present, so it is not being saved. An adviser or the firm’s owner can reopen it.',
    );
    this.name = 'PlanLockedError';
  }
}

export class FactFindConflictError extends Error {
  constructor(readonly currentVersion: number | null) {
    super(
      currentVersion === null
        ? 'No document exists yet — save with expectedVersion null to create it'
        : `The document has changed since you loaded it (now version ${currentVersion}) — reload before saving`,
    );
    this.name = 'FactFindConflictError';
  }
}

/**
 * The plan holds an imported fact that must be resolved before anything is
 * calculated (remediation spec 2026-09-17 §8) — today, a meeting note that
 * suggests the client pays Scottish income tax. The browser refuses exactly
 * where the server does (`import_review_blocked` on every projection route),
 * with the same words, so the screen never shows figures the server would
 * refuse to produce.
 */
export class ImportReviewBlockedError extends Error {
  constructor(readonly blockers: readonly ImportReviewBlocker[]) {
    super(
      blockers.length > 1
        ? `${blockers[0]!.message} (${blockers.length - 1} more imported ${blockers.length === 2 ? 'fact needs' : 'facts need'} review.)`
        : (blockers[0]?.message ?? 'An imported fact needs review before the plan is calculated.'),
    );
    this.name = 'ImportReviewBlockedError';
  }
}

/** The API's code for a save that would delete an open tax-region question. */
export const IMPORT_REVIEW_REGION_REMOVED = 'import_review_region_removed';

/**
 * A save refused because it would remove an open tax-region question imported
 * from a meeting note (spec 2026-09-17 §8). Carries the server's own plain
 * message — which says where to answer the question — rather than a generic
 * HTTP failure.
 */
export class ImportReviewRegionRemovedError extends Error {
  constructor(message: string) {
    super(
      message.trim() !== ''
        ? message
        : 'This save would remove the open tax-region question imported from a meeting note. Answer it on the People section instead.',
    );
    this.name = 'ImportReviewRegionRemovedError';
  }
}

/** Throws `ImportReviewBlockedError` when the document may not be calculated yet. */
export function assertCalculable(document: HouseholdFactFindDocument): void {
  const blockers = importReviewBlockers(document.importReview);
  if (blockers.length > 0) throw new ImportReviewBlockedError(blockers);
}

/**
 * Run the REAL projection engine on a fact-find document, in the browser —
 * the live-editing hot path. Identical to the API preview route: the same
 * wire mapping, the same reviewed rule set for the start tax year, the same
 * response contract (disclaimer REQUIRED), so no UI can render figures the
 * server would not produce.
 */
export function runHouseholdPreview(
  document: HouseholdFactFindDocument,
  clientId: string,
  asOfDate: IsoDate,
  startYear: number,
  options: {
    includeTrace?: boolean;
    firmPresets?: FirmAssumptionPresets | null;
    /**
     * The figures behind every `setVersionId` this document names, fetched
     * from the server's own resolver. A pinned plan whose version is missing
     * here is REFUSED by the wire mapping rather than run on a product
     * bundle, which is why the caller waits for the fetch instead of
     * previewing without it.
     */
    pinnedSets?: readonly PinnedAssumptionSetWire[] | null;
  } = {},
): HouseholdProjectionPreviewResponse {
  assertCalculable(document);
  const rules = rulesForTaxYear(startYear);
  const clock = makeClock(isoDate(asOfDate), startYear);
  const result = projectHousehold(
    buildHouseholdInput(
      projectionRequestFromDocument(document, { clientId, asOfDate, startYear }),
      {
        firmPresets: options.firmPresets ?? undefined,
        pinnedSets: options.pinnedSets ?? undefined,
      },
    ),
    clock,
    rules,
  );
  const { trace: _trace, ...lean } = result;
  return householdProjectionPreviewResponseSchema.parse({
    ...(options.includeTrace === true ? result : lean),
    disclaimer: ILLUSTRATIVE_NOT_ADVICE_DISCLAIMER,
  });
}

/**
 * The plain-English derivation of ONE projected year — the same engine, the
 * same `explainYear()` the API's explain endpoint and the PDF appendix use,
 * run locally so the drill-down answers "why is spending £X this year"
 * without a round-trip. Returns null when the year (or the document) cannot
 * be explained rather than throwing into the render path.
 */
/**
 * Derivation lines from an ALREADY-COMPUTED trace-bearing result — no extra
 * engine run. The wire response is structurally the engine result (the same
 * cast the report model makes for its appendix); `explainYear` reads only
 * `years`/`trace`/`warnings`/`taxRulesVersion`. Returns null when the year is
 * outside the window or the result carries no trace.
 */
export function explainProjectedYear(
  result: HouseholdProjectionPreviewResponse,
  year: number,
): readonly string[] | null {
  try {
    return explainYear(result as unknown as HouseholdProjectionResult, year);
  } catch {
    return null;
  }
}

export function explainHouseholdYear(
  document: HouseholdFactFindDocument,
  clientId: string,
  asOfDate: IsoDate,
  startYear: number,
  year: number,
  options: {
    firmPresets?: FirmAssumptionPresets | null;
    pinnedSets?: readonly PinnedAssumptionSetWire[] | null;
  } = {},
): readonly string[] | null {
  try {
    assertCalculable(document);
    const rules = rulesForTaxYear(startYear);
    const clock = makeClock(isoDate(asOfDate), startYear);
    const result = projectHousehold(
      buildHouseholdInput(
        projectionRequestFromDocument(document, { clientId, asOfDate, startYear }),
        {
          firmPresets: options.firmPresets ?? undefined,
          pinnedSets: options.pinnedSets ?? undefined,
        },
      ),
      clock,
      rules,
    );
    return explainYear(result, year);
  } catch {
    return null;
  }
}
